import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Activity, TrendingUp, TrendingDown, AlertTriangle, Calendar, Euro, Clock, Info, ChevronRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ChantierMetrics } from "@/hooks/useChantierMetrics";
import { getRentabilityBadge } from "@/lib/rentabilityBadge";
import { cn } from "@/lib/utils";

interface ChantierKpisProps {
  metrics: ChantierMetrics | null;
  /** Callback pour naviguer vers un onglet du projet */
  onTabChange?: (tab: 'quote' | 'team' | 'expenses') => void;
}

export default function ChantierKpis({ metrics, onTabChange }: ChantierKpisProps) {
  const [showCoutTotal, setShowCoutTotal] = useState(false);

  if (!metrics) return null;

  const rentabilityBadge = getRentabilityBadge(metrics.marge_finale_pct || 0);
  const isClickable = !!onTabChange;

  const formatCurrency = (value: number | null | undefined) => {
    if (value == null) return '—';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value: number | null | undefined) => {
    if (value == null) return '—';
    // Afficher les décimales si présentes (ex: 3.5 jours)
    if (Number.isInteger(value)) {
      return value.toString();
    }
    return value.toFixed(1);
  };

  const isProfitable = metrics.marge_finale_pct >= 0;

  return (
    <div className="space-y-4 animate-fade-up">
      {/* Métriques principales - 4 colonnes compactes */}
      <div className="grid grid-cols-4 gap-3">
        {/* Budget HT - cliquable vers onglet Devis */}
        <Card
          className={cn(
            "relative h-full",
            isClickable && "cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-primary/30 hover:shadow-lg"
          )}
          onClick={() => onTabChange?.('quote')}
        >
          <CardContent className="p-3 h-full flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-semibold text-white uppercase">Budget HT</span>
              <Euro className="h-3 w-3 text-primary" />
            </div>
            <div className="text-lg font-bold">{formatCurrency(metrics.budget_ht)}</div>
            <div className="flex-1" />
            {isClickable && (
              <p className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                <ChevronRight className="h-2.5 w-2.5" />
                Voir le devis
              </p>
            )}
          </CardContent>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button
                className="absolute bottom-2 right-2 p-0.5 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help"
                type="button"
                onClick={(e) => e.stopPropagation()}
              >
                <Info className="h-3 w-3 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">Montant HT du devis actif. Cliquez pour voir le devis.</p>
            </HoverCardContent>
          </HoverCard>
        </Card>

        {/* Coût équipe - cliquable vers onglet Équipe */}
        <Card
          className={cn(
            "relative h-full",
            isClickable && "cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-primary/30 hover:shadow-lg"
          )}
          onClick={() => onTabChange?.('team')}
        >
          <CardContent className="p-3 h-full flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-semibold text-white uppercase">
                {showCoutTotal ? 'Coût total équipe' : 'Coût équipe engagé'}
              </span>
              <Switch
                checked={showCoutTotal}
                onCheckedChange={setShowCoutTotal}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-8 data-[state=checked]:bg-primary data-[state=unchecked]:bg-slate-500 data-[state=unchecked]:border data-[state=unchecked]:border-slate-400 [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-4"
              />
            </div>
            <div className="text-lg font-bold">
              {showCoutTotal
                ? formatCurrency(
                    // Coût total équipe = coût engagé (MO réelle) + coût prévisionnel (MO future)
                    // Note: cout_main_oeuvre_reel inclut déjà les heures sup
                    (metrics.cout_main_oeuvre_reel || 0) + (metrics.cout_mo_previsionnel || 0)
                  )
                : formatCurrency(metrics.cout_main_oeuvre_reel)
              }
            </div>
            {/* Mode OFF (Coût engagé) : afficher détail retard et heures sup si présent */}
            {!showCoutTotal && (
              <div className="space-y-0.5">
                {metrics.cout_retard_engage > 0 && (
                  <p className="text-[10px] text-orange-500">
                    dont retard: {formatCurrency(metrics.cout_retard_engage)}
                  </p>
                )}
                {(metrics.cout_heures_sup ?? 0) > 0 && (
                  <p className="text-[10px] text-amber-500">
                    dont heures sup: {formatCurrency(metrics.cout_heures_sup)}
                  </p>
                )}
              </div>
            )}
            {/* Mode ON (Coût total prévu) : afficher détails MO future, heures sup et suspension */}
            {showCoutTotal && (
              <div className="space-y-0.5">
                {(metrics.cout_mo_previsionnel || 0) > 0 && (
                  <p className="text-[10px] text-blue-500">
                    dont MO future: {formatCurrency(metrics.cout_mo_previsionnel || 0)}
                  </p>
                )}
                {(metrics.cout_heures_sup ?? 0) > 0 && (
                  <p className="text-[10px] text-amber-500">
                    dont heures sup: {formatCurrency(metrics.cout_heures_sup)}
                  </p>
                )}
                {(metrics.cout_suspension_total || 0) > 0 && (
                  <p className="text-[10px] text-purple-500">
                    dont suspension: {formatCurrency(metrics.cout_suspension_total || 0)}
                  </p>
                )}
              </div>
            )}
            <div className="flex-1" />
            {isClickable && (
              <p className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                <ChevronRight className="h-2.5 w-2.5" />
                Voir l'équipe
              </p>
            )}
          </CardContent>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button
                className="absolute bottom-2 right-2 p-0.5 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help"
                type="button"
                onClick={(e) => e.stopPropagation()}
              >
                <Info className="h-3 w-3 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">
                {showCoutTotal
                  ? "Coût total équipe sur l'ensemble du chantier. Cliquez pour gérer l'équipe."
                  : "Coût équipe déjà engagé à ce jour. Cliquez pour gérer l'équipe."}
              </p>
            </HoverCardContent>
          </HoverCard>
        </Card>

        {/* Coûts fixes - cliquable vers onglet Dépenses */}
        <Card
          className={cn(
            "relative h-full",
            isClickable && "cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-primary/30 hover:shadow-lg"
          )}
          onClick={() => onTabChange?.('expenses')}
        >
          <CardContent className="p-3 h-full flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-semibold text-white uppercase">Coûts fixes</span>
              <Euro className="h-3 w-3 text-alert" />
            </div>
            <div className="text-lg font-bold">{formatCurrency(metrics.couts_fixes_engages)}</div>
            <div className="flex-1" />
            {isClickable && (
              <p className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                <ChevronRight className="h-2.5 w-2.5" />
                Voir les dépenses
              </p>
            )}
          </CardContent>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button
                className="absolute bottom-2 right-2 p-0.5 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help"
                type="button"
                onClick={(e) => e.stopPropagation()}
              >
                <Info className="h-3 w-3 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">Total des factures fournisseurs et frais annexes. Cliquez pour voir les dépenses.</p>
            </HoverCardContent>
          </HoverCard>
        </Card>

        {/* Jours - non cliquable (pas d'onglet associé) */}
        <Card className="relative">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-semibold text-white uppercase">Jours</span>
              <Calendar className={`h-3 w-3 ${metrics.jours_ecoules > metrics.duree_estimee_jours ? 'text-danger' : 'text-primary'}`} />
            </div>
            <div className={`text-lg font-bold ${metrics.jours_ecoules > metrics.duree_estimee_jours ? 'text-danger' : ''}`}>
              {formatNumber(metrics.jours_ecoules)}/{formatNumber(metrics.duree_estimee_jours)}
              {metrics.jours_ecoules > metrics.duree_estimee_jours && (
                <span className="text-xs text-danger ml-1">
                  (+{formatNumber(metrics.jours_ecoules - metrics.duree_estimee_jours)})
                </span>
              )}
            </div>
          </CardContent>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button className="absolute bottom-2 right-2 p-0.5 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help" type="button">
                <Info className="h-3 w-3 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">Jours travaillés écoulés depuis le début / durée totale estimée. En rouge si dépassement.</p>
            </HoverCardContent>
          </HoverCard>
        </Card>
      </div>

      {/* Marge à date, Jours restants, Marge finale - 3 colonnes compactes */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-l-4 border-l-primary relative">
          <CardContent className="p-3">
            <div className="text-xs font-semibold text-white mb-1">Marge à date</div>
            <div className={`text-xl font-bold ${metrics.marge_a_date >= 0 ? 'text-success' : 'text-danger'}`}>
              {formatCurrency(metrics.marge_a_date)}
            </div>
          </CardContent>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button className="absolute bottom-2 right-2 p-0.5 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help" type="button">
                <Info className="h-3 w-3 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">
                Marge actuelle = Budget HT - (Coûts fixes + Main d'œuvre réelle + Heures sup).
                <strong> Inclut les coûts de retard et heures supplémentaires déjà engagés.</strong>
              </p>
            </HoverCardContent>
          </HoverCard>
        </Card>

        <Card className={`border-l-4 ${metrics.jours_restants_rentables != null && metrics.jours_restants_rentables <= 7 ? 'border-l-warning' : 'border-l-muted'} relative`}>
          <CardContent className="p-3">
            <div className="text-xs font-semibold text-white mb-1">Jours rentables</div>
            <div className={`text-xl font-bold ${
              metrics.jours_restants_rentables != null && metrics.jours_restants_rentables <= 3
                ? 'text-danger'
                : metrics.jours_restants_rentables != null && metrics.jours_restants_rentables <= 7
                ? 'text-warning'
                : 'text-success'
            }`}>
              {metrics.jours_restants_rentables == null ? '—' : metrics.jours_restants_rentables}
            </div>
            <p className="text-[10px] text-muted-foreground">Jours restants avant marge nulle</p>
          </CardContent>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button className="absolute bottom-2 right-2 p-0.5 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help" type="button">
                <Info className="h-3 w-3 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">Nombre de jours travaillés restants avant que le chantier devienne déficitaire au rythme actuel de dépenses.</p>
            </HoverCardContent>
          </HoverCard>
        </Card>

        <Card className="border-l-4 border-l-success relative">
          <CardContent className="p-3">
            <div className="text-xs font-semibold text-white mb-1">Marge finale estimée</div>
            <div className="flex items-baseline gap-2">
              <span className={`text-xl font-bold ${metrics.marge_finale >= 0 ? 'text-success' : 'text-danger'}`}>
                {formatCurrency(metrics.marge_finale)}
              </span>
              <span className={`text-sm font-semibold ${metrics.marge_finale_pct >= 0 ? 'text-success' : 'text-danger'}`}>
                ({metrics.marge_finale_pct?.toFixed(1)}%)
              </span>
            </div>
            {metrics.cout_retard_previsionnel > 0 && (
              <p className="text-[10px] text-orange-500">
                Retard planifié: {formatCurrency(metrics.cout_retard_previsionnel)}
              </p>
            )}
          </CardContent>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <button className="absolute bottom-2 right-2 p-0.5 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help" type="button">
                <Info className="h-3 w-3 text-muted-foreground" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
              <p className="text-sm text-foreground">
                Marge projetée à la fin du chantier.
                <strong> Inclut les coûts de retard planifiés</strong> (engagés + futurs).
                {metrics.cout_retard > 0 && (
                  <span className="block mt-1 text-orange-500">
                    Total retard: {formatCurrency(metrics.cout_retard)} (engagé: {formatCurrency(metrics.cout_retard_engage)}, planifié: {formatCurrency(metrics.cout_retard_previsionnel)})
                  </span>
                )}
              </p>
            </HoverCardContent>
          </HoverCard>
        </Card>
      </div>
    </div>
  );
}
